import { useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { CheckIcon, LanguageIcon } from './icons';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, normalizeLocale, SUPPORTED_LOCALES } from '@/i18n/constants';

/**
 * Language as an icon button with a small menu (TMMAPP-0036) — the compact header form of
 * `LanguageChips`; the choice persists the same way (i18n.ts `languageChanged`).
 */
export function LanguageMenuButton() {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LOCALE;

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={t('header.language')}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <LanguageIcon size={20} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {SUPPORTED_LOCALES.map((code) => (
          <MenuItem
            key={code}
            lang={code}
            role="menuitemradio"
            aria-checked={code === current}
            selected={code === current}
            onClick={() => {
              setAnchor(null);
              void i18n.changeLanguage(code);
            }}
          >
            <ListItemIcon>{code === current && <CheckIcon size={20} />}</ListItemIcon>
            <ListItemText>{t(`language.${code}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
